/**
 * Recommendation Microservice API client.
 *
 * Routes via nginx prefix /recommendations/api/v1/* → saf3ai-recommendation-microservice.
 *
 *   GET  /recommendations             list active recs for the current org/user
 *   GET  /recommendations/summary     counts + top pick, for a panel header
 *   POST /recommendations/{id}/dismiss
 *   POST /recommendations/{id}/snooze
 *   POST /recommendations/{id}/restore
 *   POST /recommendations/{id}/read      per-reader, not a lifecycle change
 *   POST /recommendations/{id}/feedback
 *   GET  /recommendations/{id}/provenance
 *
 *   GET  /recommendations/{id}/actions          what can be done, availability resolved
 *   POST /recommendations/{id}/actions/{key}/execute
 *   GET  /recommendations/{id}/history          what has already been done
 *   GET  /recommendations/{id}/impact           did acting on it help?
 *   GET  /recommendations/assignable-members
 *   POST /recommendations/bulk                  one lifecycle verb, many findings
 *
 *   GET  /tenant-config/rules         list catalog rules + tenant override state
 *   PUT  /tenant-config/rules/{rule_id}
 *   POST /tenant-config/rules/{rule_id}/run-now
 *   POST /tenant-config/evaluate-now  re-evaluate every rule for this tenant
 *   GET  /tenant-config/modules       modules the engine can evaluate, + counts
 *   GET  /tenant-config/catalog       facet counts for browsing the rule catalog now
 */

import axios from 'axios';
import { emitSessionExpired } from '../auth/sessionExpiry';
import { reportAxiosModuleNotLicensed } from '../../licensing/moduleLockEvents';

const API_ORIGIN =
  (typeof window !== 'undefined' && window.env?.REACT_APP_API_ORIGIN) ||
  process.env.REACT_APP_API_ORIGIN ||
  (typeof window !== 'undefined' && window.location?.origin) ||
  '';

const PREFIX = '/recommendations/api/v1';

const api = axios.create({
  baseURL: API_ORIGIN,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('jwt_token') || localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    // A 403 module_not_licensed swaps the page for its lock screen (ModuleLockBoundary).
    reportAxiosModuleNotLicensed(err);
    if (err?.response?.status === 401) emitSessionExpired?.();
    return Promise.reject(err);
  },
);

export const recommendationAPI = {
  // ── Recommendations (public) ──────────────────────────────────────────
  // Returns `{ items, total, offset }` rather than a bare array: the caller has
  // to be able to tell a complete queue from a truncated one. `total` counts
  // every finding matching the filter, not just the page.
  //
  // `indexes: null` is load-bearing. Axios serialises an array as
  // `status[]=dismissed`, which FastAPI does not read — it wants the repeated
  // `status=dismissed`. The mismatch was silent: the parameter was dropped, the
  // endpoint fell back to its default statuses, and the Snoozed / Dismissed /
  // Resolved tabs quietly showed active findings instead of failing.
  async list(params = {}) {
    const res = await api.get(`${PREFIX}/recommendations`, {
      params,
      paramsSerializer: { indexes: null },
    });
    const items = Array.isArray(res.data) ? res.data : [];
    const total = Number(res.headers?.['x-total-count']);
    return {
      items,
      // An engine that did not send the header is not claiming a total. Falling
      // back to the page length would assert "this is all of them", which is
      // the exact lie paging exists to prevent.
      total: Number.isFinite(total) ? total : null,
      offset: Number(res.headers?.['x-offset']) || 0,
    };
  },
  // `opts.skipModuleLock`: a nav badge asking about a module the org has not
  // bought must not swap the page under it for a lock screen.
  async summary(params = {}, opts = {}) {
    const res = await api.get(`${PREFIX}/recommendations/summary`, {
      params,
      ...(opts.skipModuleLock ? { skipModuleLock: true } : {}),
    });
    return res.data;
  },
  async dismiss(recId, reason) {
    const res = await api.post(`${PREFIX}/recommendations/${recId}/dismiss`, { reason });
    return res.data;
  },
  async snooze(recId, until) {
    const res = await api.post(`${PREFIX}/recommendations/${recId}/snooze`, { until });
    return res.data;
  },
  // Mark one item read for THIS person. Read state is per user; dismiss and
  // snooze are decisions the whole organization shares.
  async markRead(recId, read = true) {
    const res = await api.post(`${PREFIX}/recommendations/${recId}/read`, null, {
      params: { read },
    });
    return res.data;
  },
  // Undo a dismiss or a snooze — dismissal is a judgement, not a deletion.
  async restore(recId) {
    const res = await api.post(`${PREFIX}/recommendations/${recId}/restore`);
    return res.data;
  },
  async feedback(recId, action, context = {}) {
    const res = await api.post(`${PREFIX}/recommendations/${recId}/feedback`, { action, context });
    return res.data;
  },
  async provenance(recId) {
    const res = await api.get(`${PREFIX}/recommendations/${recId}/provenance`);
    return res.data;
  },

  // ── The action plane ──────────────────────────────────────────────────
  //
  // `actions` returns availability, not just a menu: a button that 502s
  // because Jira was never connected costs more trust than an absent one costs
  // convenience, so the engine says up front what it can and cannot do and
  // where an admin goes to fix it.
  async listActions(recId) {
    const res = await api.get(`${PREFIX}/recommendations/${recId}/actions`);
    return res.data;
  },
  // `key` can be `skill:block_agent`, so it is encoded — an unescaped colon
  // is legal in a path segment but the slash in a future namespaced key
  // would not be.
  async executeAction(recId, key, { params = {}, selectedRows = [] } = {}) {
    const res = await api.post(
      `${PREFIX}/recommendations/${recId}/actions/${encodeURIComponent(key)}/execute`,
      { params, selected_rows: selectedRows },
    );
    return res.data;
  },
  async actionHistory(recId) {
    const res = await api.get(`${PREFIX}/recommendations/${recId}/history`);
    return res.data;
  },
  // "You acted 6 days ago; critical agents 14 → 3." The one question an inbox
  // has to answer if anyone is going to keep using it.
  async impact(recId) {
    const res = await api.get(`${PREFIX}/recommendations/${recId}/impact`);
    return res.data;
  },
  async assignableMembers() {
    const res = await api.get(`${PREFIX}/recommendations/assignable-members`);
    return res.data;
  },
  // Lifecycle verbs only. Ticketing and skills are deliberately absent: forty
  // calls to a third party will partially fail, and a partial failure reported
  // as one number is how somebody believes forty tickets exist when eleven do.
  async bulk(recIds, action, params = {}) {
    const res = await api.post(`${PREFIX}/recommendations/bulk`, {
      rec_ids: recIds,
      action,
      params,
    });
    return res.data;
  },

  // ── Tenant rule config ────────────────────────────────────────────────
  async listRules() {
    const res = await api.get(`${PREFIX}/tenant-config/rules`);
    return res.data;
  },
  async updateRule(ruleId, payload) {
    const res = await api.put(`${PREFIX}/tenant-config/rules/${ruleId}`, payload);
    return res.data;
  },
  async runRuleNow(ruleId) {
    const res = await api.post(`${PREFIX}/tenant-config/rules/${ruleId}/run-now`);
    return res.data;
  },
  // The modules this engine can actually evaluate, with each one's catalog
  // rule count and this tenant's open count. Served by the engine so the UI
  // cannot drift from the adapters that exist.
  async listModules() {
    const res = await api.get(`${PREFIX}/tenant-config/modules`);
    return res.data;
  },
  // Facet counts for the rule catalog — by discipline, surface, severity, tag
  // and effort. Computed server-side so the numbers are the same rules the
  // evaluator runs, with this tenant's overrides applied.
  async getCatalog() {
    const res = await api.get(`${PREFIX}/tenant-config/catalog`);
    return res.data;
  },
  // Per rule: has it fired, is it merely quiet, or can it not fire here at all?
  // Answers "why have I never seen this rule?" without reading logs — the page
  // previously showed a healthy-and-quiet rule and a rule missing its signal
  // identically, as one that had simply never appeared.
  async getCoverage() {
    const res = await api.get(`${PREFIX}/tenant-config/coverage`);
    return res.data;
  },
  // How often this tenant's estate is re-evaluated, when it last ran and when
  // it next will. The cadence returned here is the one the scheduler enforces.
  async getSchedule() {
    const res = await api.get(`${PREFIX}/tenant-config/schedule`);
    return res.data;
  },
  // Set the org-wide cadence. `null` clears it and returns every rule to the
  // cadence its catalog entry asks for.
  async updateSchedule(cadenceMinutes) {
    const res = await api.put(`${PREFIX}/tenant-config/schedule`, {
      cadence_minutes: cadenceMinutes ?? null,
    });
    return res.data;
  },
  // Force a full evaluation pass for this tenant. `module` narrows it to one
  // surface (e.g. 'atlas') so a page refresh does not re-run every module.
  async evaluateNow(module) {
    const res = await api.post(`${PREFIX}/tenant-config/evaluate-now`, null, {
      params: module ? { module } : {},
    });
    return res.data;
  },
};

export default recommendationAPI;
